// AutorBiografia.js — Página /autores/:id (perfil público do autor).
// Redesenhada conforme mockup:
//   - Breadcrumb: Início › Autores › Nome
//   - Avatar circular centralizado + nome + função
//   - Abas "Publicações" | "Sobre"
//       Publicações → grid do card único do site (CardDestaque)
//       Sobre       → "Sobre a Autora", biografia, formação, contato/redes
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import CardDestaque from "../produto/cardDestaque/CardDestaque";
import MarcaPadrao from "../ui/MarcaPadrao";
import { Giros, Ornamento } from "../ui/TituloDeSecao";

// Resolve a URL da foto do autor (full URL ou arquivo público da API)
const resolveFoto = (a, API_URL) => {
  const f = a?.foto || a?.imagem || a?.avatar || a?.profile_image || a?.image || null;
  if (!f) return null;
  return /^https?:\/\//i.test(f) ? f : `${API_URL}public/${f}`;
};

const AutorBiografia = () => {
  const { id } = useParams();
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aba, setAba] = useState("publicacoes"); // "publicacoes" | "sobre"

  const main = process.env.REACT_APP_MAIN;
  const API_URL = process.env.REACT_APP_API;

  useEffect(() => {
    const fetchAuthorData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_URL}autor/${id}`);
        if (!response.ok) throw new Error("Autor não encontrado");
        setAuthor(await response.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAuthorData();
  }, [id, API_URL]);

  if (loading) {
    return (
      <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] text-center text-white/60">
        Carregando...
      </div>
    );
  }

  if (error || !author) {
    return (
      <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] flex flex-col items-center text-white">
        <h1 className="titulo-display text-[2.3rem]">Autor não encontrado.</h1>
        <Link to={main + "autores"} className="mt-4 text-insigne-orange hover:underline">
          Voltar para Autores
        </Link>
      </div>
    );
  }

  const foto = resolveFoto(author, API_URL);
  // A API retorna as publicações do autor em `ebooks_como_autor` (snake_case).
  const livros = author.ebooks_como_autor || author.ebooksComoAutor || author.livros || author.ebooks || [];
  const formacao = author.formacao || author.referencias || null;

  const redes = [
    { url: author.site || author.website, icon: "uil-globe" },
    { url: author.facebook, icon: "uil-facebook-f" },
    { url: author.instagram, icon: "uil-instagram" },
    { url: author.linkedin, icon: "uil-linkedin-alt" },
    { url: author.twitter, icon: "uil-twitter" },
    { url: author.youtube, icon: "uil-youtube" },
    { url: author.lattes, icon: "uil-graduation-cap" },
  ].filter((r) => r.url);

  const TabBtn = ({ value, children }) => (
    <button
      onClick={() => setAba(value)}
      className={
        "relative px-2 pb-3 text-base transition-colors " +
        (aba === value
          ? "text-white font-semibold after:absolute after:left-0 after:right-0 after:-bottom-px after:h-[3px] after:bg-insigne-orange after:rounded-full"
          : "text-white/55 hover:text-white/80")
      }
    >
      {children}
    </button>
  );

  return (
    <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-16 text-white">
      <div className="max-w-[1150px] mx-auto">
        {/* Breadcrumb */}
        <nav className="text-xs text-white/60 mb-10 flex gap-2 flex-wrap">
          <Link to={main} className="hover:!text-insigne-orange !text-white/60">Início</Link>
          <span>›</span>
          <Link to={main + "autores"} className="hover:!text-insigne-orange !text-white/60">Autores</Link>
          <span>›</span>
          <span className="text-insigne-orange">{author.nome || "Autor"}</span>
        </nav>

        {/* Cabeçalho centralizado: avatar + nome + função */}
        <div className="flex flex-col items-center text-center">
          {/* O anel tracejado que gira em volta da foto: o mesmo do mentor,
              no site principal. Ver Giros em ui/TituloDeSecao. */}
          <div className="relative mt-[2.5rem]">
          <Giros className="left-1/2 top-1/2 h-[11rem] w-[11rem] -translate-x-1/2 -translate-y-1/2 md:h-[13rem] md:w-[13rem]" />
          <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden
                          border-2 border-line bg-insigne-navy-soft flex items-center justify-center">
            {foto ? (
              <img src={foto} alt={author.nome} className="w-full h-full object-cover" />
            ) : (
              <MarcaPadrao />
            )}
          </div>
          </div>
          <span className="chapeu chapeu-centro mt-[3rem]">Autor Insigne</span>
          <h1 className="titulo-display mb-0 mt-[0.6rem] text-[2.8rem] md:text-[4rem]">
            {author.nome || "Nome não informado"}
          </h1>
          <Ornamento className="mt-[0.9rem]" />
          {(author.funcao || author.profissao) && (
            <p className="mt-1 text-white/60">{author.funcao || author.profissao}</p>
          )}
        </div>

        {/* Abas */}
        <div className="mt-8 flex items-center justify-center gap-10 border-b border-line">
          <TabBtn value="publicacoes">Publicações</TabBtn>
          <TabBtn value="sobre">Sobre</TabBtn>
        </div>

        {/* ============ Conteúdo das abas ============ */}
        <div className="mt-10">
          {aba === "publicacoes" ? (
            Array.isArray(livros) && livros.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 md:gap-x-6 gap-y-12 orfa-ao-centro">
                {livros.map((livro, i) => (
                  <CardDestaque key={livro.id ?? i} livro={livro} />
                ))}
              </div>
            ) : (
              <p className="text-center text-white/50 py-10">
                Este autor ainda não possui publicações cadastradas.
              </p>
            )
          ) : (
            <div className="max-w-[820px] mx-auto">
              {/* Sobre */}
              <h2 className="titulo-display text-[2rem] mb-3">
                Sobre {author.nome ? `${author.nome.split(" ")[0]}` : "o Autor"}
              </h2>
              <p className="text-white/80 leading-relaxed whitespace-pre-line">
                {author.biografia || author.bio || "Biografia não disponível."}
              </p>

              {/* Formação e referências (apenas se houver) */}
              {formacao && (
                <>
                  <h2 className="titulo-display text-[2rem] mt-8 mb-3">Formação e referências:</h2>
                  <p className="text-white/80 leading-relaxed whitespace-pre-line">{formacao}</p>
                </>
              )}

              {/* Contato + redes sociais */}
              {(author.email || redes.length > 0) && (
                <div className="mt-10 pt-6 border-t border-line">
                  {author.email && (
                    <a
                      href={`mailto:${author.email}`}
                      className="inline-flex items-center gap-2 text-insigne-orange hover:underline"
                    >
                      <i className="bi bi-envelope" />
                      {author.email}
                    </a>
                  )}
                  {redes.length > 0 && (
                    <ul className="flex items-center gap-3 mt-4">
                      {redes.map((r) => (
                        <li key={r.icon}>
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-full flex items-center justify-center
                                       bg-white/5 border border-line text-white/90
                                       hover:bg-insigne-orange hover:text-ink-fixed transition-colors"
                          >
                            <i className={`uil ${r.icon} text-xl`} />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutorBiografia;
